import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Login from './pages/login';
import ProfilePage from './pages/profile';
import AddCompanyPage from './pages/company';
import UsersPage from './pages/users/index';
import CompaniesPage from './pages/companies';
import DashboardPage from './pages/dashboard';
import LandingPage from './pages/landing';
import CompanyDetailsPage from './pages/company-details';
import LineOfBusiness from './pages/lineOfBusiness';
import AboutPage from './pages/about';
import ContactPage from './pages/contact';
import ComparePage from './pages/compare';
import { TeamAssistantThemeProvider } from './context/TeamAssistantThemeContext';
import { TeamAssistantQueryPersistProvider } from './libs/TeamAssistantQueryPersistProvider';
import JobTypePage from './pages/jobType';
import JobsPage from './pages/jobs';
import TeamAssistantPage from './pages/assistant';
import { logout } from './redux/userSlice';
import { canAccessApp, hasRole, homePathForRole } from './utils/roles';

const DATA_ROLES = ['admin', 'staff'];

const PrivateRoute = ({ element, isAuthenticated, requiredRole }) => {
  const location = useLocation();
  const { currentUser } = useSelector((state) => state.user);

  if (!isAuthenticated || !canAccessApp(currentUser)) {
    return <Navigate to="/login" state={{ prevUrl: location.pathname + location.search }} replace />;
  }

  if (requiredRole && !hasRole(currentUser, requiredRole)) {
    return <Navigate to={homePathForRole(currentUser?.role)} replace />;
  }

  return element;
};

const PublicRoute = ({ element, isAuthenticated }) => {
  const { currentUser } = useSelector((state) => state.user);
  return isAuthenticated && canAccessApp(currentUser)
    ? <Navigate to={homePathForRole(currentUser?.role)} replace />
    : element;
};

function App() {
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.user);
  const isAuthenticated = Boolean(currentUser?.token) && canAccessApp(currentUser);

  useEffect(() => {
    if (currentUser && !canAccessApp(currentUser)) {
      localStorage.removeItem('token');
      dispatch(logout());
    }
  }, [currentUser, dispatch]);

  return (
    <TeamAssistantQueryPersistProvider>
      <TeamAssistantThemeProvider>
      <div className="App">
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute
                  isAuthenticated={isAuthenticated}
                  element={<Login />}
                />
              }
            />
            <Route
              path="/"
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  element={<LandingPage />}
                />
              }
            />
            <Route
              path="/company/:id"
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  element={<CompanyDetailsPage />}
                />
              }
            />


            {/* Protected Routes */}
            <Route
              path="/profile"
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  element={<ProfilePage />}
                />
              }
            />
            
           
        
            <Route
              path="/admin/users"
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  requiredRole="admin"
                  element={<UsersPage />}
                />
              }
            />

            <Route
              path="/admin/jobs"
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  requiredRole={DATA_ROLES}
                  element={<JobsPage />}
                />
              }
            />

            <Route
              path="/admin/team-assistant"
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  requiredRole={DATA_ROLES}
                  element={<TeamAssistantPage />}
                />
              }
            />

            <Route
              path="/admin/team-assistant/oversight"
              element={<Navigate to="/admin/team-assistant" replace />}
            />

            <Route
              path="/admin/job-type"
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  requiredRole={DATA_ROLES}
                  element={<JobTypePage />}
                />
              }
            />

            <Route
              path="/admin/companies"
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  requiredRole={DATA_ROLES}
                  element={<CompaniesPage />}
                />
              }
            />

            <Route
              path="/admin/line-of-business"
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  requiredRole={DATA_ROLES}
                  element={<LineOfBusiness />}
                />
              }
            />

            <Route
              path="/admin/company/add"
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  requiredRole={DATA_ROLES}
                  element={<AddCompanyPage />}
                />
              }
            />

            <Route
              path="/admin/company/:id/edit"
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  requiredRole={DATA_ROLES}
                  element={<AddCompanyPage />}
                />
              }
            />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  requiredRole="admin"
                  element={<DashboardPage />}
                />
              }
            />

            <Route
              path="*"
              element={<Navigate to={isAuthenticated ? homePathForRole(currentUser?.role) : '/login'} replace />}
            />

            <Route
              path="/about"
              element={<PrivateRoute isAuthenticated={isAuthenticated} element={<AboutPage />} />}
            />
            <Route
              path="/contact"
              element={<PrivateRoute isAuthenticated={isAuthenticated} element={<ContactPage />} />}
            />
            {/* <Route path="/compare" element={<ComparePage />} /> */}
            <Route
              path="/line-of-business"
              element={<PrivateRoute isAuthenticated={isAuthenticated} element={<LineOfBusiness />} />}
            />
          </Routes>
        </Router>
      </div>
      </TeamAssistantThemeProvider>
    </TeamAssistantQueryPersistProvider>
  );
}

export default App;
